import React, { useState, useEffect } from 'react';
import { Button, Grid } from '@mui/material';
import AutoSuggestList from './AutoSuggestList';
import CommonDialog from '../CommonDialog/CommonDialog';
import { getLibraryByUserId } from '../../services/libraryService';
import { getCellsByLibraryId } from '../../services/cellService';
import { getViewsByCellId } from '../../services/viewService';
import { PREDEFINED_LIBRARIES } from "../../constants/Constants";

const VIEW_TYPE_ID = 3;

const EditCellDiagram = ({ editDialogOpen, onClose, onCreate, onStickCreate }) => {
  const [librarySearch, setLibrarySearch] = useState('');
  const [cellSearch, setCellSearch] = useState('');
  const [viewSearch, setViewSearch] = useState('');

  const [libraries, setLibraries] = useState([]);
  const [cells, setCells] = useState([]);
  const [views, setViews] = useState([]);

  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedView, setSelectedView] = useState(null);

  const userId = localStorage.getItem('user_id')

  useEffect(() => {
    if (editDialogOpen) {
      fetchLibraries();
      resetAll();
    }
  }, [editDialogOpen]);

  useEffect(() => {
    if (selectedLibrary?.library_id) {
      fetchCells(selectedLibrary.library_id);
    } else {
      setCells([]);
      setViews([]);
    }
  }, [selectedLibrary]);

  useEffect(() => {
    if (selectedCell?.cell_id) {
      fetchViews(selectedCell.cell_id);
    } else {
      setViews([]);
    }
  }, [selectedCell]);

  const resetAll = () => {
    setLibrarySearch('');
    setCellSearch('');
    setViewSearch('');
    setSelectedLibrary(null);
    setSelectedCell(null);
    setSelectedView(null);
  };

  const fetchLibraries = async () => {
    try {
      const response = await getLibraryByUserId(userId);
      if(!response.success) return;  
      const data = response.body;
      setLibraries(data);
    } catch (error) {
      console.error("Failed to fetch libraries:", error);
    }
  };

  const fetchCells = async (libraryId) => {
    try {
      const response = await getCellsByLibraryId(libraryId);
      if (!response.success) return;
      const data = response.body;
      setCells(data);
    } catch (error) {
      console.error(`Failed to fetch cells for library ${libraryId}:`, error);
    }
  };

  const fetchViews = async (cellId) => {
    try {
      const response = await getViewsByCellId(cellId);
      if(!response.success) return;
      const data = response.body;
      setViews(data);
    } catch (error) {
      console.error(`Failed to fetch views for cell ${cellId}:`, error);
    }
  };

  const filteredLibraries = libraries.filter((lib) =>
    lib.name.toLowerCase().includes(librarySearch.toLowerCase())
  );

  const filteredCells = cells.filter((cell) =>
    cell.name.toLowerCase().includes(cellSearch.toLowerCase())
  );

  const filteredViews = views.filter((view) =>
    view.name.toLowerCase().includes(viewSearch.toLowerCase())
  );

  const handleEditCell = () => {
    if (!selectedView) return;

    if(selectedView.view_type_id === VIEW_TYPE_ID){
      const payload = {
        library_name: selectedLibrary?.name || '',
        cell_name: selectedCell?.name || '',
        view_id: selectedView.view_id,
        view_name: selectedView.name,
        cell_id: selectedCell.cell_id
      };
      onStickCreate(payload);
      onClose();
    } 
    else {
      const payload = {
        cell_name: selectedCell.name,
        view_id: selectedView.view_id,
        view_type_id: selectedView.view_type_id,
        view_name: selectedView.name,
        cell_id: selectedCell.cell_id
      };

      onCreate(payload);
      onClose();
    }
  };

const isPredefinedLibrary = selectedLibrary && PREDEFINED_LIBRARIES.includes(selectedLibrary.name.toLowerCase());

  return (
    <CommonDialog open={editDialogOpen} onClose={onClose} title="Open or Edit Cell" hideBackDrop={true} width="830px">
      <>
        <Grid container spacing={2} pt={1} pb={1}>
          <Grid item xs={4} sx={{ display: "flex", flexDirection: "column" }}>
            <AutoSuggestList
              label="Library List"
              placeholder="Search library"
              searchValue={librarySearch}
              onSearch={setLibrarySearch}
              items={filteredLibraries}
              selectedItem={selectedLibrary}
              onSelect={(lib) => {
                setSelectedLibrary(lib);
                setSelectedCell(null);
                setSelectedView(null);
                // Reset search terms
                setCellSearch("");
                setViewSearch("");
              }}
              type="library"
            />
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", flexDirection: "column" }}>
            <AutoSuggestList
              label="Cell List"
              placeholder="Search cell"
              searchValue={cellSearch}
              onSearch={setCellSearch}
              items={filteredCells}
              selectedItem={selectedCell}
              onSelect={(cell) => {
                setSelectedCell(cell);
                setSelectedView(null);
                setViewSearch("");
              }}
              disabled={!selectedLibrary}
              type="cell"
            />
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", flexDirection: "column" }}>
            <AutoSuggestList
              label="View List"
              placeholder="Search view"
              searchValue={viewSearch}
              onSearch={setViewSearch}
              items={filteredViews}
              selectedItem={selectedView}
              onSelect={setSelectedView}
              disabled={!selectedCell}
              type="view"
            />
          </Grid>
        </Grid>
      </>
      <>
        <Button
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="customButtonStyle"
          sx={{ ml: 2 }}
          disabled={!selectedLibrary || !selectedCell || !selectedView || isPredefinedLibrary}
          onClick={handleEditCell}
        >
          Open/Edit
        </Button>
      </>
    </CommonDialog>
  );
};

export default EditCellDiagram;